<!--
  VoiceChat Component

  This component provides the voice chat interface using the new class-based architecture.
  It implements the full-featured voice chat with all available tools.
-->

<template>
  <!-- Main container with exact same sizing as TaskBox -->
  <div class="w-full max-w-md task-display"
    style="width: 100% !important; margin: 0 auto; padding: 0; box-sizing: border-box;">
    <!-- Primary Mode Button -->
    <template v-if="mode === 'primary'">
      <div @click="handleClick"
        :class="{
          'task-box border border-white rounded-xl p-4 bg-transparent min-h-[100px] flex flex-col justify-center items-center w-full cursor-pointer': true,
          'voice-button--green': !voiceChat.isSessionActive.value && !voiceChat.isConnecting.value && !voiceChat.hasError.value && !voiceChat.isShuttingDown.value,
          'voice-button--blue': voiceChat.isSessionActive.value && !voiceChat.isShuttingDown.value,
          'voice-button--yellow': voiceChat.isConnecting.value && !voiceChat.isSessionActive.value && !voiceChat.isShuttingDown.value,
          'voice-button--red': voiceChat.hasError.value && !voiceChat.isShuttingDown.value,
          'voice-button--orange': voiceChat.isShuttingDown.value,
          'opacity-70 cursor-not-allowed': isProcessing || (voiceChat.isConnecting.value && !voiceChat.isSessionActive.value) || voiceChat.isShuttingDown.value
        }"
        :aria-disabled="isProcessing || (voiceChat.isConnecting.value && !voiceChat.isSessionActive.value) || voiceChat.isShuttingDown.value">
        <div class="voice-button--primary w-full bg-transparent flex items-center justify-center">
          <span class="button-icon" style="display: flex; align-items: center;">
            <UIcon :name="buttonIcon" />
          </span>
          <span class="voicechat-text font-bold">{{ buttonText }}</span>
        </div>
      </div>
    </template>

    <!-- Overlay mode button (rounded square) -->
    <template v-else>
      <button @click="handleClick" 
        class="voice-button voice-button--overlay"
      :class="{
        'voice-button--overlay-default': !voiceChat.isSessionActive.value && !voiceChat.isConnecting.value && !voiceChat.hasError.value && !voiceChat.isShuttingDown.value,
        'voice-button--overlay-active': voiceChat.isSessionActive.value && !voiceChat.isShuttingDown.value,
        'voice-button--overlay-connecting': voiceChat.isConnecting.value && !voiceChat.isSessionActive.value && !voiceChat.isShuttingDown.value,
        'voice-button--overlay-error': voiceChat.hasError.value && !voiceChat.isShuttingDown.value,
        'voice-button--overlay-shutting-down': voiceChat.isShuttingDown.value
      }"
      :disabled="isProcessing || (voiceChat.isConnecting.value && !voiceChat.isSessionActive.value) || voiceChat.isShuttingDown.value"
      :aria-label="buttonAriaLabel">
      <div class="flex items-center justify-center h-full w-full">
        <UIcon :name="buttonIcon" class="button-icon" />
      </div>
    </button>
    </template>

    <!-- Error tooltip -->
    <div v-if="voiceChat.hasError.value && !voiceChat.isConnecting.value && !voiceChat.isSessionActive.value"
      class="error-tooltip">
      {{ voiceChat.errorMessage.value || 'An error occurred' }}
    </div>

    <!-- First-time user tooltip -->
    <div v-if="showTooltip" class="help-tooltip">
      {{ t('components.voiceChat.tooltips.main') }}
      <button class="tooltip-close" @click="dismissTooltip">×</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, shallowRef } from 'vue'
import type { ToolOverridesMap } from '../../app/components/VoiceChat/tools/ToolRegistry' // Type imports first
import { SeniorCalendarVoiceChat } from '../../app/components/VoiceChat/implementations/SeniorCalendarVoiceChat'
import { MelkFullVoiceChat } from '../../app/components/VoiceChat/implementations/MelkFullVoiceChat'
import { createVoiceChat } from '../../app/components/VoiceChat/core/createVoiceChat'
import { VoiceChatBase } from '../../app/components/VoiceChat/core/VoiceChatBase'
import { useLogger } from '~/composables/useLogger' // Import after local modules
import { useAuthStore } from '~/stores/authStore'
import { useI18n } from 'vue-i18n'
import { useCentralConfigStore } from '~/stores/centralConfigStore'
// --- Props ---

const props = defineProps({
  mode: {
    type: String,
    default: 'overlay', // Default to the smaller overlay size
    validator: (value: string) => ['primary', 'overlay'].includes(value)
  },
  useFullFeatured: {
    type: Boolean,
    default: null // Default to null to use centralConfigStore setting
  }
})

// --- Emits ---
const emit = defineEmits(['click'])

// --- Local State ---

const isProcessing = ref(false) // Prevents rapid clicks
const showTooltip = ref(false)
const authStore = useAuthStore()
const centralConfigStore = useCentralConfigStore()
const logger = useLogger()
const { t } = useI18n()

// --- Voice Chat Instance ---

// Custom tool overrides specific to this component
const customToolOverrides: ToolOverridesMap = {
  // Example:
  // 'getCurrentTime': {
  //   description: 'Get the current local time and timezone information',
  //   continuationInstructions: 'Acknowledge the current time and ask if the user needs any assistance.'
  // }
}

const { isUseFullFeaturedChat } = storeToRefs(centralConfigStore)

// Helper function to create voice chat instance
const createVoiceChatInstance = (useFullFeatured: boolean) => {
  return useFullFeatured
    ? createVoiceChat<MelkFullVoiceChat>(MelkFullVoiceChat, {
        voice: 'ash',
        toolOverrides: customToolOverrides,
        implementationType: 'cal_full'
      })
    : createVoiceChat(SeniorCalendarVoiceChat, {
        voice: 'alloy',
        toolOverrides: customToolOverrides,
        implementationType: 'cal_senior'
      });
}

// Create voice chat instance with custom options based on initial feature flag
const voiceChat = shallowRef<VoiceChatBase>(
  createVoiceChatInstance(props.useFullFeatured !== null ? props.useFullFeatured : isUseFullFeaturedChat.value)
);

// Watch for changes to isUseFullFeaturedChat and update voiceChat instance
watch([isUseFullFeaturedChat, () => props.useFullFeatured], ([newConfigValue, newPropValue]) => {
  // If prop is explicitly set (not null), it takes precedence over the store value
  const shouldUseFullFeatured = newPropValue !== null ? newPropValue : newConfigValue;
  voiceChat.value = createVoiceChatInstance(shouldUseFullFeatured);
});

// --- Computed Properties for Button State ---

// Map our button states to UButton color prop values
const buttonStateClass = computed(() => {
  if (props.mode === 'primary') {
    // For UButton's color prop - restrict to valid ButtonColor values
    if (voiceChat.value.isShuttingDown.value) return 'orange';
    if (voiceChat.value.hasError.value) return 'red';
    if (voiceChat.value.isSessionActive.value) return 'blue';
    if (voiceChat.value.isConnecting.value) return 'yellow';
    return 'green'; // Default state
  } else {
    // For overlay mode's custom CSS classes
    if (voiceChat.value.isShuttingDown.value) return 'voice-button--shutting-down';
    if (voiceChat.value.hasError.value) return 'voice-button--error';
    if (voiceChat.value.isSessionActive.value) return 'voice-button--active';
    if (voiceChat.value.isConnecting.value) return 'voice-button--connecting';
    return 'voice-button--default';
  }
});

const buttonText = computed(() => {
  if (voiceChat.value.isShuttingDown.value) return t('components.voiceChat.ending')
  if (voiceChat.value.hasError.value) return t('components.voiceChat.error')
  if (voiceChat.value.isSessionActive.value) return t('components.voiceChat.listening')
  if (voiceChat.value.isConnecting.value) return t('components.voiceChat.connecting')
  return t('components.voiceChat.button_text')
})

const buttonIcon = computed(() => {
  if (voiceChat.value.isShuttingDown.value) return 'i-heroicons-hand-raised' // Hand raised for "goodbye" gesture
  if (voiceChat.value.hasError.value) return 'i-heroicons-exclamation-triangle'
  if (voiceChat.value.isSessionActive.value) return 'i-heroicons-microphone-solid' // Solid mic when active
  if (voiceChat.value.isConnecting.value) return 'i-heroicons-arrow-path' // Spinner/loading icon
  return 'i-heroicons-microphone' // Outline mic by default
})

const buttonAriaLabel = computed(() => {
  if (voiceChat.value.isShuttingDown.value) return t('components.voiceChat.aria.ending')
  if (voiceChat.value.hasError.value) return t('components.voiceChat.aria.error', { error: voiceChat.value.errorMessage.value || 'Unknown error' })
  if (voiceChat.value.isSessionActive.value) return t('components.voiceChat.aria.stop')
  if (voiceChat.value.isConnecting.value) return t('components.voiceChat.aria.connecting')
  return t('components.voiceChat.aria.start')
})

// --- Methods ---

const dismissTooltip = () => {
  showTooltip.value = false
  localStorage.setItem('voiceChatTooltipSeen', 'true')
}

const handleClick = async () => {
  logger.info('VoiceChat: Button clicked')
  
  // Emit click event for parent component to handle non-logged in users
  emit('click')
  
  // Only proceed with voice chat functionality if user is logged in
  if (!authStore.userLoggedIn) {
    logger.info('VoiceChat: User not logged in, letting parent component handle it')
    return
  }
  
  // Otherwise continue with normal voice chat toggle
  await toggleSession()
}

const toggleSession = async () => {
  if (isProcessing.value) {
    logger.info('VoiceChat: Ignoring click while processing')
    return
  }
  
  // Don't allow toggling when already shutting down
  if (voiceChat.value.isShuttingDown.value) {
    logger.info('VoiceChat: Ignoring click while shutting down')
    return
  }

  isProcessing.value = true
  logger.info('VoiceChat: Toggling session', { 
    active: voiceChat.value.isSessionActive.value, 
    connecting: voiceChat.value.isConnecting.value, 
    error: voiceChat.value.hasError.value,
    shuttingDown: voiceChat.value.isShuttingDown.value
  })

  try {
    if (voiceChat.value.isSessionActive.value) {
      logger.info('VoiceChat: Stopping session...')
      await voiceChat.value.stopSession() // Make sure stop can be async if needed
      logger.info('VoiceChat: Session stopped.')
    } else if (!voiceChat.value.isConnecting.value) { // Prevent starting if already connecting
      logger.info('VoiceChat: Starting session...')
      await voiceChat.value.startSession()
      logger.info('VoiceChat: Session started.')
    }
  } catch (error) {
    logger.error('VoiceChat: Error toggling session:', error)
    // Error state is handled by voiceChat.hasError reactive property
  } finally {
    // Use a timer to prevent immediate re-clicks, especially if start/stop is very fast
    setTimeout(() => {
      isProcessing.value = false
    }, 300) // Shorter delay might be okay
  }
}

// --- Lifecycle Hooks ---

onMounted(() => {
  logger.info('VoiceChat: Component mounted', { 
    mode: props.mode,
    // Debug voice chat state
    isSessionActive: voiceChat.value.isSessionActive.value,
    isConnecting: voiceChat.value.isConnecting.value,
    hasError: voiceChat.value.hasError.value,
    buttonState: buttonStateClass.value
  })

  // Log state changes for debugging
  watch(() => voiceChat.value.isSessionActive.value, (newValue) => {
    logger.info('VoiceChat: isSessionActive changed', { 
      newValue, 
      buttonState: buttonStateClass.value 
    })
  })
  
  watch(() => voiceChat.value.isConnecting.value, (newValue) => {
    logger.info('VoiceChat: isConnecting changed', { 
      newValue, 
      buttonState: buttonStateClass.value 
    })
  })
  
  watch(() => voiceChat.value.isShuttingDown.value, (newValue) => {
    logger.info('VoiceChat: isShuttingDown changed', { 
      newValue, 
      buttonState: buttonStateClass.value 
    })
  })

  // Show tooltip for first-time users
  const hasSeenTooltip = localStorage.getItem('voiceChatTooltipSeen')
  if (!hasSeenTooltip && props.mode === 'overlay') { // Only show tooltip on overlay mode
    setTimeout(() => {
      // Ensure component is still mounted
      if (voiceChat.value) { // Basic check
        showTooltip.value = true
      }
    }, 1500) // Slightly longer delay
  }
})

onBeforeUnmount(() => {
  logger.info('VoiceChat: Component unmounting')
  voiceChat.value.cleanup() // Ensure resources are released
})
</script>

<style scoped>
/* Voice Chat Container - ensures consistent width */
.voice-chat-container {
  position: relative;
  box-sizing: border-box;
  margin: 0 auto;
  max-width: min(90vw, 448px);
  padding: 0;
}

/* Custom styles for primary button */
.voice-button--primary {
  font-weight: 700;
  color: white;
  border: none;
  width: 100%;
  margin: 0 auto;
  letter-spacing: 0.025em;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: transparent;
  min-height: 80px;
}

/* Responsive text sizing for voice chat button */
.voicechat-text {
  font-size: clamp(1.5rem, min(4vh, 4vw), 2rem);
  line-height: 1;
}

/* Responsive icon sizing */
.button-icon {
  font-size: clamp(1.75rem, min(5vh, 5vw), 2.5rem);
  margin-right: clamp(0.5rem, min(2vh, 2vw), 1rem);
}

/* Button color states - applied to task-box container */
.voice-button--green {
  background-color: #007500;
}

.voice-button--green:hover {
  background-color: #00A000;
}

.voice-button--blue {
  background-color: #3B82F6; /* Blue for active/listening state */
  animation: subtle-pulse 2s infinite ease-in-out;
}

.voice-button--blue:hover {
  background-color: #2563EB;
}

.voice-button--yellow {
  background-color: #EAB308; /* Darker yellow for connecting state */
}

.voice-button--yellow:hover {
  background-color: #CA8A04;
}

.voice-button--red {
  background-color: #EF4444;
}

.voice-button--red:hover {
  background-color: #DC2626;
}

.voice-button--orange {
  background-color: #F97316; /* Orange for shutting down state */
  animation: fade-pulse 1.5s infinite ease-in-out;
}

/* Disabled state - applied to the container */
.task-box:has(.voice-button--primary:disabled) {
  opacity: 0.7;
  cursor: not-allowed;
  pointer-events: none;
}

/* Spinner animation for connecting state */
.voice-button--yellow .button-icon {
  animation: spin 1s linear infinite;
}

/* Overlay mode styles - now a rounded square */
.voice-button--overlay {
  width: 60px;
  height: 60px;
  padding: 10px;
  border-radius: 12px; /* Rounded square instead of circle */
  background-color: #007500;
  border: 1px solid white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2); /* Add subtle shadow for depth */
}

.voice-button--overlay .button-icon {
  font-size: clamp(1.5rem, min(4vh, 4vw), 1.75rem);
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  margin-right: 0 !important; /* Override the default margin for the overlay mode */
}

/* Overlay button states */
.voice-button--overlay-default {
  background-color: #007500;
}

.voice-button--overlay-default:hover:not(:disabled) {
  background-color: #00A000;
}

.voice-button--overlay-active {
  background-color: #3B82F6; /* Blue for active/listening state - matches primary mode */
  animation: subtle-pulse 2s infinite ease-in-out;
}

.voice-button--overlay-connecting {
  background-color: #EAB308; /* Darker yellow for connecting state - matches primary mode */
}

.voice-button--overlay-connecting .button-icon {
  animation: spin 1s linear infinite;
}

.voice-button--overlay-error {
  background-color: #EF4444;
}

.voice-button--overlay-shutting-down {
  background-color: #F97316; /* Orange for shutting down state */
  animation: fade-pulse 1.5s infinite ease-in-out;
}

.voice-button--overlay:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Tooltips */
.error-tooltip,
.help-tooltip {
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.875rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 21;
  animation: fadeIn 0.3s ease-out;
  pointer-events: none;
  white-space: nowrap;
}

.error-tooltip {
  background-color: rgba(239, 68, 68, 0.95);
  color: white;
  width: auto;
}

.help-tooltip {
  background-color: #333;
  color: white;
  width: 200px;
  pointer-events: auto;
  white-space: normal;
}

/* Tooltip arrow */
.error-tooltip::after,
.help-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px;
  border-style: solid;
}

.error-tooltip::after {
  border-color: rgba(239, 68, 68, 0.95) transparent transparent transparent;
}

.help-tooltip::after {
  border-color: #333 transparent transparent transparent;
}

.tooltip-close {
  position: absolute;
  top: 4px;
  right: 4px;
  background: none;
  border: none;
  color: white;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 2px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translate(-50%, 5px);
  }

  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes subtle-pulse {
  0% {
    background-color: #3B82F6; /* Start with normal blue */
  }
  50% {
    background-color: #60A5FA; /* Slightly lighter blue at peak */
  }
  100% {
    background-color: #3B82F6; /* Back to normal blue */
  }
}

@keyframes fade-pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}

/* Responsive styling */
@media (orientation: portrait) {
  .voice-button--primary {
    min-height: 80px !important;
  }
  
  .voicechat-text {
    font-size: clamp(1.25rem, min(3.5vh, 3.5vw), 1.75rem);
  }
  
  .button-icon {
    font-size: clamp(1.5rem, min(4vh, 4vw), 2.25rem);
  }
}

@media (max-height: 640px) and (orientation: portrait) {
  .voice-button--primary {
    min-height: 70px !important;
    padding: 0.75rem !important;
  }
  
  .voicechat-text {
    font-size: clamp(1.125rem, min(3vh, 3vw), 1.5rem);
  }
  
  .button-icon {
    font-size: clamp(1.375rem, min(3.5vh, 3.5vw), 2rem);
    margin-right: 0.625rem;
  }
}

/* iPhone landscape mode - specifically targeting low-height landscape mode */
@media (max-height: 450px) and (orientation: landscape) {
  .task-box {
    min-height: 70px !important;
    padding: 0.5rem !important;
  }
  
  .voice-button--primary {
    min-height: 60px !important;
  }
  
  .voicechat-text {
    font-size: 1.25rem !important;
  }
  
  .button-icon {
    font-size: 1.75rem !important;
    margin-right: 0.5rem !important;
  }
  
  /* Smaller overlay button for landscape */
  .voice-button--overlay {
    width: 50px !important;
    height: 50px !important;
    padding: 6px !important;
  }
  
  .voice-button--overlay .button-icon {
    font-size: 1.35rem !important;
  }
}
</style>
